// Admin UI for managing a tenant's survey forms (the `forms` table) — add,
// rename, and remove the survey forms available to a selected tenant.
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { IoAddCircleOutline, IoCheckmark, IoClose, IoPencil, IoTrashOutline } from "react-icons/io5";
import client from "../api/client";
import { SectionCard } from "./ThemeColorFields";
import TableSkeleton from "./TableSkeleton";
import ButtonLoader from "./ButtonLoader";
import { withMinDuration, reloadIfCurrentTenant } from "../utils";

const SurveyGroupAssignment = ({ tenantId, tenants = [], onTenantChange = () => {}, lockedTenant = null }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [newFormId, setNewFormId] = useState("");
  const [newTabName, setNewTabName] = useState("");
  const [adding, setAdding] = useState(false);

  const loadData = async (isStale = () => false) => {
    if (!tenantId) {
      setForms([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await withMinDuration(client.get(`/tenants/${tenantId}/survey-forms`));
      if (isStale()) return;
      setForms(res.data.forms_all || []);
    } catch (error) {
      if (isStale()) return;
      toast.error(error.response?.data?.message || "Failed to load surveys.");
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  // Guards against a slower response for a since-abandoned tenant selection
  // landing after a newer one and overwriting it with stale data.
  useEffect(() => {
    let cancelled = false;
    loadData(() => cancelled);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const handleAddForm = async (e) => {
    e.preventDefault();
    if (!tenantId) {
      toast.error("Select a tenant first.");
      return;
    }
    if (!newFormId.trim() || !newTabName.trim()) return;
    setAdding(true);
    try {
      await client.post(`/tenants/${tenantId}/survey-forms`, {
        forms: [{ form_id: newFormId.trim(), tab_name: newTabName.trim() }],
      });
      setNewFormId("");
      setNewTabName("");
      toast.success("Survey form added.");
      reloadIfCurrentTenant(tenantId);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add survey form.");
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (form) => {
    setEditingId(form.id);
    setEditValue(form.form_tab || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveEdit = async (form) => {
    if (!tenantId) return;
    setSavingId(form.id);
    try {
      const res = await client.put(`/tenants/${tenantId}/survey-forms/${form.id}/assignment`, {
        form_tab: editValue.trim(),
      });
      setForms((prev) => prev.map((f) => (f.id === res.data.id ? res.data : f)));
      toast.success("Survey form updated.");
      reloadIfCurrentTenant(tenantId);
      cancelEdit();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update survey form.");
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (form) => {
    if (!tenantId) return;
    setSavingId(form.id);
    try {
      await client.delete(`/tenants/${tenantId}/survey-forms/${form.id}`);
      setForms((prev) => prev.filter((f) => f.id !== form.id));
      toast.success("Survey form removed.");
      reloadIfCurrentTenant(tenantId);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to remove survey form.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <SectionCard
      title="Survey Groups"
      description="Manage the survey forms available to a tenant. Select a tenant, then add, rename, or remove its survey forms."
    >
      <div className="flex flex-col gap-6">
        {!lockedTenant && (
          <div className="flex flex-col gap-2 w-[320px]">
            <h6 className="text-canvas-directoryText">Tenant</h6>
            <select
              className="border border-canvas-tersery rounded p-3 text-sm font-outfit h-[42px]"
              value={tenantId || ""}
              onChange={(e) => onTenantChange(e.target.value ? Number(e.target.value) : null)}
            >
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
              ))}
            </select>
          </div>
        )}

        {tenantId && (
          <div className="flex flex-col gap-4">
            <div className="border border-canvas-tersery rounded p-4">
              <h6 className="text-canvas-directoryText mb-3">Add a survey form</h6>
              <form onSubmit={handleAddForm} className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1.5 flex-1 min-w-[220px]">
                  <label className="font-outfit text-xs text-canvas-secondery font-bold uppercase">Form ID</label>
                  <input
                    className="border border-canvas-tersery rounded p-3 text-sm font-outfit focus:outline-none focus:border-canvas-primary"
                    placeholder="HubSpot survey form ID"
                    value={newFormId}
                    onChange={(e) => setNewFormId(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5 flex-1 min-w-[220px]">
                  <label className="font-outfit text-xs text-canvas-secondery font-bold uppercase">Tab Name</label>
                  <input
                    className="border border-canvas-tersery rounded p-3 text-sm font-outfit focus:outline-none focus:border-canvas-primary"
                    placeholder="Display name for this form"
                    value={newTabName}
                    onChange={(e) => setNewTabName(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  disabled={adding || !newFormId.trim() || !newTabName.trim()}
                  className="flex items-center gap-2 bg-canvas-primary rounded px-5 h-[46px] font-outfit font-semibold text-canvas-white uppercase text-xs disabled:cursor-not-allowed disabled:opacity-50 shrink-0"
                >
                  {adding ? <ButtonLoader /> : <IoAddCircleOutline className="w-4 h-4" />}
                  {adding ? "Adding..." : "Add Form"}
                </button>
              </form>
            </div>

            <div className="border border-canvas-tersery rounded overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-canvas-tersery/40">
                    <thead className="bg-canvas-userbar">
                      <tr>
                        <th className="px-4 py-3 text-start text-xs text-canvas-secondery font-outfit font-bold uppercase">Tab Name</th>
                        <th className="px-4 py-3 text-start text-xs text-canvas-secondery font-outfit font-bold uppercase">Form ID</th>
                        <th className="px-4 py-3 text-end text-xs text-canvas-secondery font-outfit font-bold uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-canvas-tersery/40">
                      {loading ? (
                        <TableSkeleton rows={4} columns={3} />
                      ) : (
                      <>
                      {forms.map((form) => (
                        <tr key={form.id} className="hover:bg-canvas-userbar/60 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap font-outfit text-sm">
                            {editingId === form.id ? (
                              <input
                                autoFocus
                                className="border border-canvas-primary rounded p-2 text-sm font-outfit w-full max-w-[240px] focus:outline-none"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                              />
                            ) : (
                              form.form_tab
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap font-outfit text-sm text-canvas-directoryText">{form.form}</td>
                          <td className="px-4 py-3 whitespace-nowrap font-outfit text-sm">
                            {editingId === form.id ? (
                              <div className="flex justify-end gap-3">
                                <button
                                  type="button"
                                  aria-label="Save"
                                  disabled={savingId === form.id || !editValue.trim()}
                                  onClick={() => saveEdit(form)}
                                  className="text-canvas-success disabled:opacity-50"
                                >
                                  {savingId === form.id ? <ButtonLoader /> : <IoCheckmark className="w-5 h-5" />}
                                </button>
                                <button
                                  type="button"
                                  aria-label="Cancel"
                                  onClick={cancelEdit}
                                  className="text-canvas-secondery"
                                >
                                  <IoClose className="w-5 h-5" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-end gap-4">
                                <button
                                  type="button"
                                  aria-label="Edit"
                                  disabled={savingId === form.id}
                                  onClick={() => startEdit(form)}
                                  className="text-canvas-primary disabled:opacity-50"
                                >
                                  <IoPencil className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  aria-label="Delete"
                                  disabled={savingId === form.id}
                                  onClick={() => handleDelete(form)}
                                  className="text-canvas-danger disabled:opacity-50"
                                >
                                  <IoTrashOutline className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {forms.length === 0 && (
                        <tr>
                          <td colSpan={3} className="px-4 py-6 font-outfit text-sm text-canvas-directoryText text-center">
                            No survey forms yet for this tenant.
                          </td>
                        </tr>
                      )}
                      </>
                      )}
                    </tbody>
                  </table>
                </div>
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
};

export default SurveyGroupAssignment;
